import { useState } from 'react'
import type { Position } from '@/types'

interface NewTradeFormProps {
  accountSlug: string
  positions?: Position[]
  initialData?: Partial<{
    ticker: string
    action: string
    priceWhenBought: string
    strikePrice: string
    premium: string
    qty: string
    buyDate: string
    expDate: string
    commission: string
    fees: string
    delta: string
    iv: string
  }>
  onSubmit: (trade: any) => void
  onCancel: () => void
}

export function NewTradeForm({ accountSlug, positions = [], initialData, onSubmit, onCancel }: NewTradeFormProps) {
  const [form, setForm] = useState({
    ticker: initialData?.ticker || '',
    action: initialData?.action || 'CSP',
    priceWhenBought: initialData?.priceWhenBought || '',
    strikePrice: initialData?.strikePrice || '',
    premium: initialData?.premium || '',
    qty: initialData?.qty || '1',
    buyDate: initialData?.buyDate || new Date().toISOString().split('T')[0],
    expDate: initialData?.expDate || '',
    commission: initialData?.commission || '0.65',
    fees: initialData?.fees || '0.02',
    positionId: '',
    delta: initialData?.delta || '',
    iv: initialData?.iv || '',
  })
  const [error, setError] = useState('')

  // Find matching positions for the entered ticker when action is CC
  const tickerPositions = form.action === 'CC' && form.ticker
    ? positions.filter(p => p.ticker.toUpperCase() === form.ticker.toUpperCase() && p.status === 'open')
    : []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.ticker || !form.strikePrice || !form.premium || !form.expDate) {
      setError('Ticker, Strike, Premium, and Exp Date are required')
      return
    }

    // If CC and multiple positions exist, require selection
    if (form.action === 'CC' && tickerPositions.length > 1 && !form.positionId) {
      setError('Please select which position this CC is written against')
      return
    }

    const premium = parseFloat(form.premium)
    const qty = parseInt(form.qty)
    const commission = -Math.abs(parseFloat(form.commission) || 0)
    const fees = -Math.abs(parseFloat(form.fees) || 0)
    const total = (premium * 100 * qty) + commission + fees

    const buyDate = new Date(form.buyDate)
    const expDate = new Date(form.expDate)
    const originalDays = Math.ceil((expDate.getTime() - buyDate.getTime()) / (1000 * 60 * 60 * 24))

    // Auto-select position if only one exists
    const positionId = form.positionId || (tickerPositions.length === 1 ? tickerPositions[0].id : undefined)

    // Delta: if ≥ 1 (whole number like 1873), divide by 10000. Always store to 4 decimal places.
    let deltaValue: number | null = null
    if (form.delta) {
      const raw = parseFloat(form.delta)
      deltaValue = raw >= 1 ? parseFloat((raw / 10000).toFixed(4)) : parseFloat(raw.toFixed(4))
    }

    onSubmit({
      ticker: form.ticker.toUpperCase(),
      action: form.action,
      priceWhenBought: parseFloat(form.priceWhenBought) || 0,
      strikePrice: parseFloat(form.strikePrice),
      currentPrice: 0,
      status: 'open',
      buyDate: form.buyDate,
      expDate: form.expDate,
      closingMonth: expDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).replace(' ', '-'),
      originalDays,
      daysLeft: originalDays,
      daysKept: null,
      premium,
      qty,
      commission,
      fees,
      total: Math.round(total * 100) / 100,
      closeAction: null,
      closeDate: null,
      closePremium: null,
      closeCommission: null,
      closeFees: null,
      closeTotal: null,
      netPosition: 0,
      returnPct: 0,
      delta: deltaValue,
      iv: form.iv ? parseFloat(form.iv) : null,
      ...(positionId ? { positionId } : {}),
    })
  }

  const renderField = (label: string, name: string, type = 'text', placeholder = '') => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">{label}</label>
      <input
        type={type}
        value={(form as any)[name]}
        onChange={(e) => setForm(f => ({ ...f, [name]: name === 'ticker' ? e.target.value.toUpperCase() : e.target.value }))}
        placeholder={placeholder}
        className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:outline-none"
        style={{ height: '40px' }}
      />
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-5 sm:p-8 w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-[var(--color-text-primary)]">New Trade</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">{accountSlug.toUpperCase()} Account</p>
        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {renderField('Ticker', 'ticker', 'text', 'NVDA')}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Action</label>
              <select
                value={form.action}
                onChange={(e) => setForm(f => ({ ...f, action: e.target.value }))}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)]"
                style={{ height: '40px' }}
              >
                <option value="CSP">CSP (Cash-Secured Put)</option>
                <option value="CC">CC (Covered Call)</option>
                <option value="Buy">Buy (LEAP)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {renderField('Price When Bought', 'priceWhenBought', 'number', '200.00')}
            {renderField('Strike Price', 'strikePrice', 'number', '180')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {renderField('Premium', 'premium', 'number', '3.50')}
            {renderField('Quantity', 'qty', 'number', '1')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {renderField('Buy Date', 'buyDate', 'date')}
            {renderField('Expiration Date', 'expDate', 'date')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {renderField('Commission', 'commission', 'number', '-0.65')}
            {renderField('Fees', 'fees', 'number', '-0.02')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {renderField('Delta', 'delta', 'number', '0.1800')}
            {renderField('IV %', 'iv', 'number', '35.2')}
          </div>

          {/* Position linking for CCs */}
          {form.action === 'CC' && tickerPositions.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Position</label>
              <select
                value={form.positionId || (tickerPositions.length === 1 ? tickerPositions[0].id : '')}
                onChange={(e) => setForm(f => ({ ...f, positionId: e.target.value }))}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)]"
                style={{ height: '40px' }}
                tabIndex={0}
              >
                {tickerPositions.length > 1 && <option value="">Select position...</option>}
                {tickerPositions.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.quantity} shares @ ${p.costBasis.toFixed(2)} (acquired {p.acquisitionDate})
                  </option>
                ))}
              </select>
              <span className="text-xs text-[var(--color-text-muted)]">
                Link this CC to a share position
              </span>
            </div>
          )}

          {error && <p className="text-sm text-[var(--color-red)]">{error}</p>}

          <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-[var(--color-border)]">
            <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]" style={{ height: '44px' }}>Cancel</button>
            <button type="submit" className="px-6 py-2.5 rounded-lg bg-[var(--color-accent)] text-white font-medium hover:bg-[var(--color-accent)]/80" style={{ height: '44px' }}>Add Trade</button>
          </div>
        </form>
      </div>
    </div>
  )
}
